import { ref, type Ref } from 'vue';
import { isKnownCountryCode, NO_SEARCH_LOCATION, type SearchLocation } from '@/helpers/schoolLocations';

type GeoResponse = {
	countryCode?: unknown;
	state?: unknown;
};

export const detectSearchLocation = async (fetchFn: typeof fetch = fetch): Promise<SearchLocation> => {
	try {
		const response = await fetchFn('/geo', { headers: { Accept: 'application/json' } });
		if (!response.ok) {
			return NO_SEARCH_LOCATION;
		}
		const body = (await response.json()) as GeoResponse;
		if (typeof body.countryCode !== 'string' || !isKnownCountryCode(body.countryCode)) {
			return NO_SEARCH_LOCATION;
		}
		const state = typeof body.state === 'string' && body.state.trim().length > 0 ? body.state.trim() : null;
		return { countryCode: body.countryCode, state: state };
	} catch {
		return NO_SEARCH_LOCATION;
	}
};

let detected: Promise<SearchLocation> | null = null;

export const useSearchLocation = (
	detect: () => Promise<SearchLocation> = () => (detected ??= detectSearchLocation()),
): {
	location: Ref<SearchLocation>;
	setLocation: (location: SearchLocation) => void;
	whenDetected: Promise<void>;
} => {
	const location = ref<SearchLocation>(NO_SEARCH_LOCATION);
	let changedByTeacher = false;

	const whenDetected = detect().then((found) => {
		if (!changedByTeacher) {
			location.value = found;
		}
	});

	const setLocation = (newLocation: SearchLocation) => {
		changedByTeacher = true;
		location.value = newLocation;
	};

	return { location: location, setLocation: setLocation, whenDetected: whenDetected };
};
